import { useState, useMemo, useEffect } from 'react';
import {
  FolderOpenIcon,
  UploadIcon,
  SearchIcon,
  PlusIcon,
  ImageIcon,
  PlayCircleIcon,
  FileTextIcon,
} from 'lucide-react';
import AssetUploadModal from '../components/AssetUploadModal';
import AssetCard from '../components/AssetCard';
import AssetPreviewModal from '../components/AssetPreviewModal';
import AssetDetailModal from '../components/AssetDetailModal';
import AssetTagEditModal from '../components/AssetTagEditModal';
import { Asset as ApiAsset, tenantApi } from '../lib/tenant-api';
import { PageLoader } from '../components/common';
import { StatCard } from '../components/StatCard';

// Local asset interface for UI
export interface Asset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'pdf' | 'other';
  size: number;
  uploadDate: string;
  url: string;
  thumbnailUrl?: string;
  description?: string;
  tags: string[];
}

type FilterType = 'all' | 'image' | 'video' | 'pdf' | 'other';

// Convert API Asset to UI Asset
const convertApiAsset = (apiAsset: ApiAsset): Asset => {
  return {
    id: apiAsset.id,
    name: apiAsset.file_name,
    type: apiAsset.asset_type,
    size: apiAsset.file_size,
    uploadDate: apiAsset.created_at,
    url: apiAsset.custom_metadata.public_url,
    thumbnailUrl: apiAsset.asset_type === 'image'
      ? apiAsset.custom_metadata.public_url
      : undefined,
    description: apiAsset.custom_metadata.description,
    tags: apiAsset.tags || []
  };
};

export default function AssetsManagement() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isTagEditModalOpen, setIsTagEditModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedAssetForDetail, setSelectedAssetForDetail] = useState<Asset | null>(null);
  const [selectedAssetForTagEdit, setSelectedAssetForTagEdit] = useState<Asset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [apiAssets, tagsResponse] = await Promise.all([
          tenantApi.getAssets(),
          tenantApi.getAssetTags()
        ]);
        setAssets(apiAssets.map(convertApiAsset));
        setAvailableTags(tagsResponse.tags);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load assets. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      const matchesSearch = !searchQuery ||
        asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesFilter = filterType === 'all' || asset.type === filterType;
      const matchesTag = selectedTag === 'all' || asset.tags.includes(selectedTag);
      return matchesSearch && matchesFilter && matchesTag;
    });
  }, [assets, searchQuery, filterType, selectedTag]);

  const handleAssetUpload = async (newAssets: ApiAsset[]) => {
    setAssets(prev => [...newAssets.map(convertApiAsset), ...prev]);
  };

  const handleAssetPreview = (asset: Asset) => {
    setSelectedAsset(asset);
    setIsPreviewModalOpen(true);
  };

  const handleAssetDetail = (asset: Asset) => {
    setSelectedAssetForDetail(asset);
    setIsDetailModalOpen(true);
  };

  const handleAssetDelete = async (assetId: string) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    try {
      await tenantApi.deleteAsset(assetId);
      setAssets(prev => prev.filter(asset => asset.id !== assetId));
    } catch (err) {
      console.error('Failed to delete asset:', err);
      setError('Failed to delete asset. Please try again later.');
    }
  };

  const handleAssetEditTags = (asset: Asset) => {
    setSelectedAssetForTagEdit(asset);
    setIsTagEditModalOpen(true);
  };

  const handleTagsUpdated = (updatedAsset: Asset) => {
    setAssets(prev => prev.map(asset =>
      asset.id === updatedAsset.id ? updatedAsset : asset
    ));
    const loadAvailableTags = async () => {
      try {
        const tagsResponse = await tenantApi.getAssetTags();
        setAvailableTags(tagsResponse.tags);
      } catch (error) {
        console.error('Failed to reload available tags:', error);
      }
    };
    loadAvailableTags();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const imageCount = assets.filter(a => a.type === 'image').length;
  const videoCount = assets.filter(a => a.type === 'video').length;
  const pdfCount = assets.filter(a => a.type === 'pdf').length;

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Asset Management</h1>
            <p className="mt-2 text-sm text-gray-600">
              Upload and manage images, videos, and documents for your AI assistant
            </p>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Upload Assets
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title="Total Assets" value={assets.length} icon={FolderOpenIcon} />
          <StatCard title="Images" value={imageCount} icon={ImageIcon} />
          <StatCard title="Videos" value={videoCount} icon={PlayCircleIcon} />
          <StatCard title="PDF" value={pdfCount} icon={FileTextIcon} skipTitleCase />
        </div>

        {/* Search, Filters & Grid */}
        <div className="bg-white shadow border border-gray-200 p-6 space-y-8">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50 hover:bg-gray-100"
                placeholder="Search Asset"
              />
            </div>
            <div className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 flex justify-between items-center">
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full bg-transparent outline-none focus:outline-none"
              >
                <option value="all">Tags</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            <div className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 flex justify-between items-center">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as FilterType)}
                className="w-full bg-transparent outline-none focus:outline-none"
              >
                <option value="all">Type</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Assets heading + Grid */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Assets</h2>

            {filteredAssets.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    viewMode="grid"
                    onPreview={handleAssetPreview}
                    onDetail={handleAssetDetail}
                    onDelete={handleAssetDelete}
                    onEditTags={handleAssetEditTags}
                    formatFileSize={formatFileSize}
                  />
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <FolderOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No assets found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery || filterType !== 'all' || selectedTag !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by uploading your first asset.'}
                </p>
                {!searchQuery && filterType === 'all' && selectedTag === 'all' && (
                  <div className="mt-6">
                    <button
                      onClick={() => setIsUploadModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <UploadIcon className="h-4 w-4 mr-2" />
                      Upload Assets
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Upload Modal */}
      <AssetUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleAssetUpload}
      />

      {/* Preview Modal */}
      {selectedAsset && (
        <AssetPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          asset={selectedAsset}
          formatFileSize={formatFileSize}
        />
      )}

      {/* Detail Modal */}
      <AssetDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedAssetForDetail(null);
        }}
        asset={selectedAssetForDetail}
      />

      {/* Tag Edit Modal */}
      <AssetTagEditModal
        isOpen={isTagEditModalOpen}
        onClose={() => {
          setIsTagEditModalOpen(false);
          setSelectedAssetForTagEdit(null);
        }}
        asset={selectedAssetForTagEdit}
        onTagsUpdated={handleTagsUpdated}
      />
    </div>
  );
}
